import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import { SkeletonPulse, SkeletonBar } from "components/pages/Educator/Dashboard/GroupsSkeleton";

// Loading placeholders for session history (OKO2-3684), shaped like the real
// rows so nothing jumps when content lands: the Lessons tab's report card
// (icon, eyebrow/title block, three stat clusters, expand button) and the
// Groups tab's session card (session block, collaboration badge, score list).

const Card = styled.div`
  align-items: center;
  background-color: ${colors.neutral100};
  border-radius: 12px;
  display: flex;
  gap: 32px;
  padding: 24px;
`;

const Circle = styled(SkeletonBar)`
  border-radius: 50%;
  flex-shrink: 0;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${(p) => p.$center && "align-items: center;"}
`;

const Cluster = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const ExpandButton = styled(SkeletonBar)`
  flex-shrink: 0;
  margin-left: auto;
`;

// One stat cluster: badge icon + big number over its label.
const Stat = () => (
  <Cluster>
    <Circle $w="44px" $h="44px" />
    <Col>
      <SkeletonBar $w="48px" $h="22px" />
      <SkeletonBar $w="88px" $h="12px" />
    </Col>
  </Cluster>
);

// Lessons tab: eyebrow + title + meta lines on the left, stat clusters right.
export const LessonReportsSkeleton = ({ rows = 4 }) => (
  <SkeletonPulse role="status" aria-label="Loading lesson reports">
    {Array.from({ length: rows }, (_, i) => (
      <Card key={i}>
        <Circle $w="32px" $h="32px" />
        <Col>
          <SkeletonBar $w="160px" $h="12px" />
          <SkeletonBar $w="300px" $h="20px" />
          <SkeletonBar $w="200px" $h="12px" />
          <SkeletonBar $w="230px" $h="12px" />
        </Col>
        <Stat />
        <Stat />
        <Stat />
        <ExpandButton $w="32px" $h="32px" $r="8px" />
      </Card>
    ))}
  </SkeletonPulse>
);

// Groups tab: session title/date block, collaboration badge column, and the
// per-student score rows.
const ScoreRow = () => (
  <Cluster>
    <SkeletonBar $w="36px" $h="18px" $r="9px" />
    <SkeletonBar $w="48px" $h="14px" />
  </Cluster>
);

export const GroupSessionsSkeleton = ({ rows = 4 }) => (
  <SkeletonPulse role="status" aria-label="Loading sessions">
    {Array.from({ length: rows }, (_, i) => (
      <Card key={i}>
        <Col>
          <SkeletonBar $w="210px" $h="20px" />
          <SkeletonBar $w="160px" $h="14px" />
          <SkeletonBar $w="140px" $h="12px" />
        </Col>
        <Col $center>
          <SkeletonBar $w="130px" $h="14px" />
          <Circle $w="48px" $h="48px" />
          <SkeletonBar $w="64px" $h="18px" $r="9px" />
        </Col>
        <Col>
          <SkeletonBar $w="260px" $h="14px" />
          <ScoreRow />
          <ScoreRow />
          <ScoreRow />
        </Col>
        <ExpandButton $w="32px" $h="32px" $r="8px" />
      </Card>
    ))}
  </SkeletonPulse>
);
